/**
 * preparePhoto — the client-side photo pipeline behind the staff and client
 * photo pickers (PeopleTab.tsx, ClientPhotoDialog.tsx).
 *
 * A phone hands the picker whatever the camera roll produced: a 2-5 MB
 * JPEG (or HEIC, which most browsers cannot decode at all), occasionally a
 * PNG screenshot, sometimes the wrong kind of file entirely. This module
 * decodes whatever the browser CAN decode, downscales + re-encodes it to a
 * size that comfortably fits the photo endpoints' JSON body cap, and
 * reports back in plain English when it can't — rather than the upload
 * just silently failing, which is the defect this exists to fix.
 *
 * Pure policy wrapped around an injectable decode/encode pair
 * (PhotoPrepDeps). The default pair uses createImageBitmap + <canvas>,
 * neither of which jsdom implements, so tests inject fakes that report
 * chosen dimensions / byte sizes instead of exercising a real decoder.
 */

import { ApiError } from './api';
import { contactSentence, errorCodeOf, pageWriteText, type SupportContact } from './errorCopy';

/**
 * The photo endpoints accept a JSON body up to PHOTO_BODY_MAX_BYTES (3 MB —
 * api/_shared/validators/imageUpload.ts), carrying the image as base64
 * (~4/3 the decoded size). Staying comfortably under 700 KB of encoded
 * image data keeps the base64 body (and the small `{ data, mime }`
 * wrapper around it) well inside that cap with room to spare.
 */
export const PHOTO_MAX_UPLOAD_BYTES = 700 * 1024;

/** Longest edge a saved photo ever needs — a face, at most a shoulders-up
 *  portrait, never displayed larger than a few hundred px on screen. */
const MAX_EDGE_PX = 1024;

export type PreparedPhotoMime = 'image/jpeg' | 'image/png';

/** What `decode` hands back: the pixel size plus an opaque handle that
 *  `encode` can draw from (an ImageBitmap or HTMLImageElement in the
 *  browser; whatever a test's fake decode returns). */
export interface DecodedPhoto {
  width: number;
  height: number;
  image: unknown;
}

export interface PhotoEncodeRequest {
  image: unknown;
  mime: PreparedPhotoMime;
  /** Absent for PNG — canvas.toBlob ignores quality for a lossless format. */
  quality?: number;
  /** The exact pixel size to draw at (already clamped to never upscale). */
  width: number;
  height: number;
  /** max(width, height) — the long edge this attempt targets. */
  edgePx: number;
}

export interface PhotoPrepDeps {
  decode?: (file: File) => Promise<DecodedPhoto>;
  encode?: (request: PhotoEncodeRequest) => Promise<Blob>;
}

export type PhotoPrepRejectCode = 'not_an_image' | 'unreadable' | 'too_large';

export type PhotoPrepResult =
  | {
      ok: true;
      /** Base64 of the encoded bytes, WITHOUT a `data:` prefix — ready to
       *  drop straight into the `{ data, mime }` upload body. */
      data: string;
      mime: PreparedPhotoMime;
      bytes: number;
      width: number;
      height: number;
    }
  | { ok: false; code: PhotoPrepRejectCode; message: string };

const HEIC_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);
/** Extensions that count as an image when the OS picker hands back an empty
 *  `type` (some do, for files copied from another device). */
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'heic', 'heif']);

function extensionOf(fileName: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(fileName);
  return match ? match[1]!.toLowerCase() : '';
}

function isHeicFile(file: File): boolean {
  if (HEIC_TYPES.has(file.type.toLowerCase())) return true;
  return HEIC_EXTENSIONS.has(extensionOf(file.name));
}

function isPngFile(file: File): boolean {
  if (file.type.toLowerCase() === 'image/png') return true;
  // Some OS file pickers hand back an empty `type` for a PNG; fall back to
  // the extension rather than silently re-encoding a lossless source as JPEG.
  return file.type === '' && extensionOf(file.name) === 'png';
}

function isImageFile(file: File): boolean {
  if (file.type.toLowerCase().startsWith('image/') || isHeicFile(file)) return true;
  return file.type === '' && IMAGE_EXTENSIONS.has(extensionOf(file.name));
}

/** Friendly labels for the non-image files a photo picker actually sees (a
 *  PDF resume, a Word doc dragged in by mistake). Falls back to the
 *  extension, then the raw mime, so the message never names nothing. */
const FRIENDLY_FILE_KIND: Record<string, string> = {
  'application/pdf': 'a PDF',
  'application/msword': 'a Word document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'a Word document',
  'application/vnd.ms-excel': 'an Excel file',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'an Excel file',
  'text/plain': 'a text file',
  'text/csv': 'a CSV file',
  'application/zip': 'a ZIP file',
  'video/mp4': 'a video',
  'video/quicktime': 'a video',
};

function describeFileKind(file: File): string | null {
  const byMime = FRIENDLY_FILE_KIND[file.type.toLowerCase()];
  if (byMime) return byMime;
  const ext = extensionOf(file.name);
  if (ext) return `a .${ext.toUpperCase()} file`;
  if (file.type) return `a ${file.type} file`;
  return null;
}

function notAnImageMessage(file: File): string {
  const kind = describeFileKind(file);
  return kind
    ? `That file is ${kind}, not a photo. Choose a photo instead, or take a new one.`
    : "That file isn't a photo. Choose a photo instead, or take a new one.";
}

function heicGuidance(): string {
  return (
    "This browser can't open this kind of iPhone photo. On the iPhone, choose Settings, " +
    'Camera, Formats, Most Compatible, then take the photo again.'
  );
}

function unreadableMessage(file: File): string {
  return isHeicFile(file) ? heicGuidance() : "That photo won't open. Choose a different one, or take a new one.";
}

/** Longest-edge downscale, never upscaling past the source's own size. */
function fitWithinEdge(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** FileReader (not Blob.arrayBuffer, which jsdom doesn't implement — and is
 *  the more universally-supported of the two regardless) does the base64
 *  encoding itself via readAsDataURL; this just strips the `data:...,` lead-in. */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolvePromise, rejectPromise) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const comma = result.indexOf(',');
      resolvePromise(comma >= 0 ? result.slice(comma + 1) : '');
    };
    reader.onerror = () => rejectPromise(reader.error ?? new Error('blob_read_failed'));
    reader.readAsDataURL(blob);
  });
}

/** Decode fallback for browsers without createImageBitmap — an off-DOM
 *  <img> fed an object URL, which every evergreen browser supports. */
async function decodeViaImageElement(file: File): Promise<DecodedPhoto> {
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<DecodedPhoto>((resolvePromise, rejectPromise) => {
      const img = new Image();
      img.onload = () => resolvePromise({ width: img.naturalWidth, height: img.naturalHeight, image: img });
      img.onerror = () => rejectPromise(new Error('image_decode_failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function defaultDecode(file: File): Promise<DecodedPhoto> {
  if (typeof createImageBitmap === 'function') {
    // 'from-image' bakes in the EXIF orientation so a portrait phone photo
    // never comes out sideways — canvas drawImage otherwise ignores EXIF.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    return { width: bitmap.width, height: bitmap.height, image: bitmap };
  }
  return decodeViaImageElement(file);
}

function defaultEncode(request: PhotoEncodeRequest): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = request.width;
  canvas.height = request.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas_2d_unavailable'));
  if (request.mime === 'image/jpeg') {
    // JPEG has no transparency; without this a transparent PNG source
    // comes out on black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, request.width, request.height);
  }
  ctx.drawImage(request.image as CanvasImageSource, 0, 0, request.width, request.height);
  return new Promise<Blob>((resolvePromise, rejectPromise) => {
    canvas.toBlob(
      (blob) => (blob ? resolvePromise(blob) : rejectPromise(new Error('canvas_encode_failed'))),
      request.mime,
      request.quality,
    );
  });
}

interface EncodeStep {
  mime: PreparedPhotoMime;
  quality?: number;
  edge: number;
}

/** Quality-then-edge fallback ladder: hold the edge at MAX_EDGE_PX and step
 *  quality down twice before shrinking the edge itself. A PNG source gets
 *  one extra lossless attempt ahead of this ladder (see planFor). */
const JPEG_LADDER: EncodeStep[] = [
  { mime: 'image/jpeg', quality: 0.86, edge: MAX_EDGE_PX },
  { mime: 'image/jpeg', quality: 0.75, edge: MAX_EDGE_PX },
  { mime: 'image/jpeg', quality: 0.65, edge: MAX_EDGE_PX },
  { mime: 'image/jpeg', quality: 0.65, edge: 800 },
  { mime: 'image/jpeg', quality: 0.65, edge: 640 },
];

function planFor(file: File): EncodeStep[] {
  if (!isPngFile(file)) return JPEG_LADDER;
  return [{ mime: 'image/png', edge: MAX_EDGE_PX }, ...JPEG_LADDER];
}

/**
 * Decode `file`, downscale + encode it to fit comfortably under the photo
 * endpoints' body cap, and return it as upload-ready base64.
 *
 * Rejects up front when the file isn't something the browser calls an
 * image (`not_an_image`); reports `unreadable` when decoding or encoding
 * throws (HEIC gets its own explanation, since that's the common real
 * case); reports `too_large` only if every step of the downscale ladder
 * still lands over PHOTO_MAX_UPLOAD_BYTES.
 */
export async function preparePhoto(file: File, deps: PhotoPrepDeps = {}): Promise<PhotoPrepResult> {
  if (!isImageFile(file)) {
    return { ok: false, code: 'not_an_image', message: notAnImageMessage(file) };
  }

  const decode = deps.decode ?? defaultDecode;
  const encode = deps.encode ?? defaultEncode;

  let decoded: DecodedPhoto;
  try {
    decoded = await decode(file);
  } catch {
    return { ok: false, code: 'unreadable', message: unreadableMessage(file) };
  }

  try {
    for (const step of planFor(file)) {
      const { width, height } = fitWithinEdge(decoded.width, decoded.height, step.edge);
      const blob = await encode({
        image: decoded.image,
        mime: step.mime,
        quality: step.quality,
        width,
        height,
        edgePx: Math.max(width, height),
      });
      if (blob.size <= PHOTO_MAX_UPLOAD_BYTES) {
        return { ok: true, data: await blobToBase64(blob), mime: step.mime, bytes: blob.size, width, height };
      }
    }
  } catch {
    return { ok: false, code: 'unreadable', message: unreadableMessage(file) };
  }

  return {
    ok: false,
    code: 'too_large',
    message: 'Even resized, that photo is too big to save. Try a different one.',
  };
}

interface UploadErrorShape {
  status?: number;
  code?: string;
  limitBytes?: number;
}

/** Reads the status, the body's code and its byte limit off a photo
 *  upload's failure; every fetch helper throws ApiError, which carries all
 *  three. The body's message is never read. */
function shapeOfUploadError(err: unknown): UploadErrorShape {
  if (!(err instanceof ApiError)) return {};
  let limitBytes: number | undefined;
  const body = err.body;
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.limit_bytes === 'number') limitBytes = b.limit_bytes;
  }
  return { status: err.status, code: errorCodeOf(err) ?? undefined, limitBytes };
}

/**
 * Plain-English mapping for a photo-upload rejection on either photo door.
 * `subject` picks the noun for a 404: the staff roster row or the client
 * record. A failure with no photo-specific sentence reads the house save
 * sentence.
 */
export function photoUploadErrorMessage(
  err: unknown,
  subject: 'staff' | 'client',
  contact: SupportContact,
): string {
  const { status, code, limitBytes } = shapeOfUploadError(err);

  switch (code) {
    case 'payload_too_large': {
      return limitBytes
        ? `That photo is too big, and the limit is ${Math.round(limitBytes / (1024 * 1024))} MB. Choose a smaller photo.`
        : 'That photo is too big. Choose a smaller photo.';
    }
    case 'photo_too_large':
      return 'That photo is too big, and the limit is 2 MB. Choose a smaller photo, or take a new one.';
    case 'invalid_mime':
    case 'magic_byte_mismatch':
      return "That kind of file can't be used as a photo. Choose a regular photo, or take a new one.";
    case 'empty_photo':
      return 'That file is empty. Choose a different photo.';
    default:
      break;
  }

  if (status === 403) {
    return contactSentence("Changing this photo isn't part of your access. Ask {contact_admin} if you need it.", contact);
  }
  if (status === 404) {
    return subject === 'client'
      ? 'That client is no longer on file. Reload the page to see the current list.'
      : 'That person is no longer on the roster. Reload the page to see the current list.';
  }

  return pageWriteText(err, contact);
}
